import React from "react";

import { AUTOPILOT_COLORS } from "../autopilot/styles";

/**
 * Card de métrica PADRÃO (theme-aware): eyebrow/label + ícone tonalizado no
 * canto, valor grande e helper. Mesmo visual das telas de Indicadores
 * (Faturamento "Resumo do recorte"). Use sempre este em vez de cravar estilo.
 *
 * Props: label, value, helper, iconClassName, tone (info|success|warning|pink|danger|neutral),
 *        onClick + active (vira um tile clicável, ex.: filtro de status), style.
 *
 * O `tone` só pinta a caixa do ícone: sem `iconClassName` ele não tem efeito.
 */
// Tones por token (PO1-2724): cor e fundo acompanham tema e cor de destaque.
// `danger` (PO1-3171) é alias de `pink`: é o nome nativo dos tokens CSS
// (--everlog-status-danger) e do StatusBadge, e o fallback silencioso para
// `info` escondia o vermelho de "Reprovada"/"Vencidas" em Minhas faturas.
const TONES = {
  info: { color: AUTOPILOT_COLORS.accent, bg: "var(--theme-accent-glow)" },
  success: { color: AUTOPILOT_COLORS.statusSuccess, bg: AUTOPILOT_COLORS.statusSuccessBg },
  warning: { color: AUTOPILOT_COLORS.statusWarning, bg: AUTOPILOT_COLORS.statusWarningBg },
  pink: { color: AUTOPILOT_COLORS.statusDanger, bg: AUTOPILOT_COLORS.statusDangerBg },
  danger: { color: AUTOPILOT_COLORS.statusDanger, bg: AUTOPILOT_COLORS.statusDangerBg },
  neutral: { color: AUTOPILOT_COLORS.muted, bg: "var(--everlog-card-surface-field)" },
};

const cardStyle = {
  minWidth: 0,
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  borderRadius: 12,
  background: AUTOPILOT_COLORS.insetSurface,
  boxShadow: `inset 0 0 0 1px ${AUTOPILOT_COLORS.borderFaint}`,
  padding: "14px 15px",
  transition: "border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
};

const headerStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "12px",
};

const labelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  lineHeight: 1.35,
  textTransform: "uppercase",
};

const iconBoxStyle = {
  width: 32,
  height: 32,
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  borderRadius: 10,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const valueStyle = {
  marginTop: 12,
  color: AUTOPILOT_COLORS.title,
  fontSize: "1.34rem",
  fontWeight: 800,
  lineHeight: 1.12,
  overflowWrap: "anywhere",
};

const helperStyle = {
  marginTop: 8,
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.8rem",
  lineHeight: 1.45,
};

export default function MetricCard({
  label,
  value,
  helper,
  iconClassName,
  tone = "info",
  onClick,
  active = false,
  style,
}) {
  const t = TONES[tone] || TONES.info;
  const interactive = typeof onClick === "function";

  return (
    <div
      onClick={onClick}
      role={interactive ? "button" : undefined}
      aria-pressed={interactive ? active : undefined}
      style={{
        ...cardStyle,
        cursor: interactive ? "pointer" : "default",
        borderColor: active ? "var(--theme-accent)" : AUTOPILOT_COLORS.border,
        transform: active ? "translateY(-1px)" : "none",
        ...style,
      }}
    >
      <div style={headerStyle}>
        <span style={labelStyle}>{label}</span>
        {iconClassName ? (
          <div style={{ ...iconBoxStyle, background: t.bg, color: t.color }}>
            <i className={iconClassName} />
          </div>
        ) : null}
      </div>
      <div style={valueStyle}>{value}</div>
      {helper ? <div style={helperStyle}>{helper}</div> : null}
    </div>
  );
}
